import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Html5Qrcode } from 'html5-qrcode'

const STATE_SCANNING = 2
const STATE_PAUSED = 3

function isScannerActive(instance) {
  if (!instance) return false
  try {
    const state = typeof instance.getState === 'function' ? instance.getState() : null
    if (state === STATE_SCANNING || state === STATE_PAUSED) return true
  } catch {
    return Boolean(instance._isScanning)
  }
  return Boolean(instance._isScanning)
}

function safeStop(instance) {
  if (!instance) return Promise.resolve()
  let halt = Promise.resolve()
  try {
    if (isScannerActive(instance)) {
      halt = instance.stop()
    }
  } catch {
    halt = Promise.resolve()
  }
  return halt.catch(() => {}).then(() => {
    try {
      if (typeof instance.clear === 'function') instance.clear()
    } catch {
      // already torn down
    }
  })
}

const QRCodeScanner = ({ onScanSuccess, onScanError, children, fullscreen, autoStart }) => {
  const [isRunning, setIsRunning] = useState(false)
  const html5QrCodeRef = useRef(null)
  const qrCodeRegionRef = useRef(null)
  const cancelledRef = useRef(false)
  const onScanSuccessRef = useRef(onScanSuccess)
  const onScanErrorRef = useRef(onScanError)

  onScanSuccessRef.current = onScanSuccess
  onScanErrorRef.current = onScanError

  const config = {
    fps: 12,
    qrbox: (viewfinderWidth, viewfinderHeight) => {
      const minEdge = Math.min(viewfinderWidth, viewfinderHeight)
      const size = Math.max(180, Math.floor(minEdge * 0.7))
      return { width: size, height: size }
    }
  }

  function start() {
    if (cancelledRef.current || !qrCodeRegionRef.current) return
    if (isScannerActive(html5QrCodeRef.current)) return

    html5QrCodeRef.current = new Html5Qrcode(qrCodeRegionRef.current.id, false)
    const instance = html5QrCodeRef.current

    instance
      .start(
        { facingMode: 'environment' },
        config,
        (decoded) => onScanSuccessRef.current?.(decoded),
        (error) => onScanErrorRef.current?.(error)
      )
      .then(() => {
        if (cancelledRef.current) {
          return safeStop(instance).then(() => {
            if (html5QrCodeRef.current === instance) html5QrCodeRef.current = null
          })
        }
        setIsRunning(true)
      })
      .catch((e) => {
        console.log(e)
        if (!cancelledRef.current) {
          alert('Please allow camera access')
          setIsRunning(false)
        }
      })
  }

  function stop() {
    cancelledRef.current = true
    const instance = html5QrCodeRef.current
    html5QrCodeRef.current = null
    setIsRunning(false)
    return safeStop(instance)
  }

  useEffect(() => {
    cancelledRef.current = false
    if (autoStart) start()
    return () => {
      cancelledRef.current = true
      const instance = html5QrCodeRef.current
      html5QrCodeRef.current = null
      safeStop(instance)
    }
  }, [autoStart])

  return (
    <>
      <div className={fullscreen ? 'absolute inset-0' : 'mx-auto aspect-square w-full max-w-[280px]'}>
        <div id="qr-code-region" ref={qrCodeRegionRef} className={fullscreen ? 'h-full w-full [&>video]:h-full [&>video]:w-full [&>video]:object-cover' : ''} />
      </div>
      {children({ start, stop, isRunning })}
    </>
  )
}

QRCodeScanner.propTypes = {
  children: PropTypes.func.isRequired,
  onScanSuccess: PropTypes.func.isRequired,
  onScanError: PropTypes.func,
  fullscreen: PropTypes.bool,
  autoStart: PropTypes.bool
}

export default QRCodeScanner
